'use client';

import { useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';

import { formatDateDistanceToNowDynamic } from '../lib/utils';

/**
 * Render a "time ago" timestamp without a hydration mismatch (React #418).
 *
 * Relative strings ("3 hours ago") are computed against the current clock, so
 * a value computed during SSR and again during client hydration disagree for
 * recent items. This is acute on our `force-static` / ISR pages (the home feed
 * + article/brand surfaces): the server HTML — including the timestamp — is
 * cached for up to the `revalidate` window (1h on /home, 24h on /articles), so
 * the server value is both STALE and guaranteed to mismatch the client.
 *
 * Strategy: render a deterministic, timezone-independent ABSOLUTE date on the
 * server + the first client render — `formatInTimeZone(..., 'UTC', ...)` is
 * identical regardless of the runtime timezone, so SSR and hydration agree
 * byte-for-byte and there is no mismatch — then upgrade to the live relative
 * string after mount, recomputed client-side so it is always fresh (never
 * frozen at cache-generation time, which is why `suppressHydrationWarning`
 * was the wrong tool here). The post-mount upgrade is driven by component
 * state, so it flows through `React.memo()` wrappers (memo gates parent-prop
 * re-renders, not a component's own state updates).
 */
export function useRelativeDate(
  date: Date | string | null | undefined,
): string | null {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!date) return null;

  // Guard malformed input: `new Date('garbage')` is a truthy Invalid Date that
  // slips past the `!date` check above and then THROWS inside formatInTimeZone /
  // date-fns ("Invalid time value"), which would crash the card during SSR.
  // Return null instead — same falsy value on server + client, so it stays
  // hydration-safe AND never crashes on corrupt upstream data.
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;

  if (!mounted) {
    // SSR + first client render: UTC-pinned absolute date. Deterministic
    // across the server (UTC) and any client timezone → zero #418.
    return formatInTimeZone(parsed, 'UTC', 'MMMM dd, yyyy');
  }

  // Client, after mount only: the live relative string ("3 hours ago"), or the
  // local absolute date for items older than the formatter's threshold.
  return formatDateDistanceToNowDynamic(date).replace('about ', '');
}
